<template>
  <b-modal
    :visible="value"
    size="lg"
    centered
    hide-footer
    :title="title"
    @change="$emit('input', $event)"
  >
    <div
      v-if="!readonly"
      class="attachment-dropzone"
      :class="{ 'attachment-dropzone--active': dragging }"
      role="button"
      tabindex="0"
      aria-label="Upload attachments by dropping files or browsing"
      @click="$refs.fileInput.click()"
      @keydown.enter.prevent="$refs.fileInput.click()"
      @keydown.space.prevent="$refs.fileInput.click()"
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <feather-icon
icon="UploadCloudIcon"
size="32"
class="text-primary mb-50"
/>
      <strong>Drag &amp; drop files here</strong>
      <span class="text-muted">or click to browse files</span>
      <small class="text-muted mt-25">Maximum 100 MB per file</small>
      <input
        ref="fileInput"
        class="sr-only"
        type="file"
        multiple
        @change="onSelect"
      >
    </div>

    <div
v-if="!readonly && queue.length"
class="mt-1"
>
      <div class="d-flex align-items-center justify-content-between mb-50">
        <strong>Files ready to upload ({{ queue.length }})</strong>
        <b-button
          size="sm"
          variant="primary"
          :disabled="uploading"
          @click="upload"
        >
          <b-spinner
v-if="uploading"
small
class="mr-50"
/>
          Upload Files
        </b-button>
      </div>
      <div
v-for="(file, index) in queue"
:key="fileKey(file)"
class="queued-file"
>
        <div class="min-width-0">
          <strong class="file-name">{{ file.name }}</strong>
          <small class="d-block text-muted">{{ fileSize(file.size) }}</small>
        </div>
        <b-button
          variant="flat-danger"
          size="sm"
          :disabled="uploading"
          :aria-label="`Remove ${file.name}`"
          @click.stop="removeQueued(index)"
        >
          <feather-icon icon="XIcon" />
        </b-button>
      </div>
    </div>

    <hr>
    <div class="d-flex align-items-center justify-content-between mb-75">
      <strong>Saved files</strong>
      <b-badge variant="light-primary">
{{ attachments.length }} files
</b-badge>
    </div>

    <div
v-if="loading"
class="text-center py-3"
>
      <b-spinner variant="primary" />
      <div class="text-muted mt-50">
Loading attachments...
</div>
    </div>
    <div
v-else-if="!attachments.length"
class="attachment-empty"
>
      <feather-icon
icon="FileIcon"
size="30"
/>
      <span>No attachments uploaded yet.</span>
    </div>
    <div
v-else
class="attachment-list"
>
      <div
        v-for="item in attachments"
        :key="attachmentId(item)"
        class="attachment-item"
      >
        <b-avatar
:variant="`light-${fileVariant(attachmentPath(item))}`"
size="42"
>
          <feather-icon :icon="fileIcon(attachmentPath(item))" />
        </b-avatar>
        <div class="attachment-meta">
            <strong class="file-name">{{ displayName(attachmentPath(item)) }}</strong>
            <small class="text-muted">{{ fileType(attachmentPath(item)) }}</small>
            <b-badge
              v-if="isMissing(item)"
              variant="light-danger"
              class="missing-file-badge"
            >
              File is missing
            </b-badge>
        </div>
        <div class="attachment-actions">
          <b-button
            v-if="!isMissing(item) && canPreview(attachmentPath(item))"
            size="sm"
            variant="outline-primary"
            @click="$emit('view', item)"
          >
            <feather-icon
icon="EyeIcon"
class="mr-25"
/> View
          </b-button>
          <b-button
            v-if="!isMissing(item)"
            size="sm"
            variant="outline-secondary"
            @click="$emit('download', item)"
          >
            <feather-icon
icon="DownloadIcon"
class="mr-25"
/> Download
          </b-button>
          <b-button
            v-if="!readonly && deletable"
            size="sm"
            variant="flat-danger"
            :disabled="String(deletingId) === String(attachmentId(item))"
            :aria-label="`Delete ${displayName(attachmentPath(item))}`"
            @click="$emit('delete', item)"
          >
            <b-spinner
              v-if="String(deletingId) === String(attachmentId(item))"
              small
            />
            <feather-icon
v-else
icon="TrashIcon"
/>
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BAvatar, BBadge, BButton, BModal, BSpinner,
} from 'bootstrap-vue'

const MAX_FILE_SIZE = 100 * 1024 * 1024

export default {
  components: {
    BAvatar,
    BBadge,
    BButton,
    BModal,
    BSpinner,
  },
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: 'Attachments' },
    attachments: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    resetKey: { type: Number, default: 0 },
    readonly: { type: Boolean, default: false },
    deletable: { type: Boolean, default: true },
    deletingId: { type: [String, Number], default: '' },
  },
  data() {
    return {
      dragging: false,
      queue: [],
    }
  },
  watch: {
    resetKey() {
      this.queue = []
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
  },
  methods: {
    addFiles(files) {
      const incoming = Array.from(files || [])
      const oversized = incoming.filter(file => file.size > MAX_FILE_SIZE)
      const existing = new Set(this.queue.map(this.fileKey))
      const valid = incoming.filter(file => (
        file.size <= MAX_FILE_SIZE && !existing.has(this.fileKey(file))
      ))
      this.queue.push(...valid)
      if (oversized.length) this.$emit('invalid-files', oversized)
    },
    onDrop(event) {
      this.dragging = false
      this.addFiles(event.dataTransfer.files)
    },
    onSelect(event) {
      this.addFiles(event.target.files)
      event.target.value = ''
    },
    removeQueued(index) {
      this.queue.splice(index, 1)
    },
    upload() {
      if (this.queue.length && !this.uploading) this.$emit('upload', [...this.queue])
    },
    fileKey(file) {
      return `${file.name}-${file.size}-${file.lastModified}`
    },
    attachmentId(item) {
      return item.InvoiceAttachmentID || item.id || item.filename || item.url
    },
    attachmentPath(item) {
      return item.InvoiceAttachment || item.filename || item.url || ''
    },
    isMissing(item) {
      return item && (item.FileMissing === true || item.StorageStatus === 'missing')
    },
    fileSize(bytes) {
      if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    displayName(path) {
      const stored = String(path || '').split('/').pop()
      return stored.replace(/^[0-9a-f]{32}_/i, '')
    },
    extension(path) {
      return this.displayName(path).split('.').pop().toLowerCase()
    },
    fileType(path) {
      const ext = this.extension(path)
      return ext ? `${ext.toUpperCase()} file` : 'File'
    },
    canPreview(path) {
      return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'txt'].includes(this.extension(path))
    },
    fileIcon(path) {
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(this.extension(path))
        ? 'ImageIcon' : 'FileTextIcon'
    },
    fileVariant(path) {
      return this.extension(path) === 'pdf' ? 'danger' : 'primary'
    },
  },
}
</script>

<style lang="scss" scoped>
.attachment-dropzone {
  display: flex;
  min-height: 150px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 2px dashed #b8c2cc;
  border-radius: 0.5rem;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover,
  &:focus,
  &--active {
    border-color: #7367f0;
    background: rgba(115, 103, 240, 0.06);
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.25);
  }
}

.queued-file,
.attachment-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.4rem;
}

.queued-file + .queued-file,
.attachment-item + .attachment-item {
  margin-top: 0.5rem;
}

.queued-file {
  justify-content: space-between;
}

.attachment-meta,
.min-width-0 {
  min-width: 0;
}

.attachment-meta {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.missing-file-badge {
  align-self: flex-start;
  margin-top: 0.25rem;
}

.file-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-actions {
  display: flex;
  gap: 0.5rem;
}

.attachment-empty {
  display: flex;
  min-height: 120px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.5rem;
  border: 1px dashed #d8d6de;
  border-radius: 0.4rem;
  color: #6e6b7b;
}

@media (max-width: 575.98px) {
  .attachment-item {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .attachment-actions {
    width: 100%;
  }

  .attachment-actions .btn:not(.btn-flat-danger) {
    flex: 1;
  }
}
</style>
